import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DateUtility, type Merchant } from '@trendiolive/trendio-selfcare-ui-kit';

import { MerchantState } from 'model/types/merchant';

import { authenticatedPost } from 'utils/axios';
import { extractFollowersTierFromMetadata, extractStarterTier, extractUnconnectedCreatorsTierRestriction } from 'utils/metadataParser';
import { MerchantSettings } from 'model/classes/MerchantSettings';

import { GET_MERCHANT_BASIC_INFO } from 'queries/get_merchant_basic_info';
import { GET_KPIS } from 'queries/get_kpis';
import { parseCollaborationMetricsFromMetadata } from 'utils/collaborationMetricsParser';

export const initialState: MerchantState = {
  displayDashboard: false,
  displayMessageCenter: false,
  merchant: null
};

export const merchantSlice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    setBasicInfo(state, action: PayloadAction<MerchantState>) {
      state.merchant = action.payload.merchant;
      state.ttsPermissionsGranted = action.payload.merchant?.onboarding?.   tiktokSellerPortalPermissionsGranted ?? false;
      state.displayMessageCenter = new MerchantSettings(action.payload.merchant?.settingsObj)
        .getSettingsBooleanAtPath(MerchantSettings.PATHS.dm_auto_reply);
      state.tiktokPlan = action.payload.tiktokPlan;
      state.youtubePlan = action.payload.youtubePlan;
      state.ttsCollaborationMetrics = parseCollaborationMetricsFromMetadata(action.payload.merchant?.metadata);
    },
    grantTTSPermissions(state, action: PayloadAction<MerchantState>) {
      state.ttsPermissionsGranted = action.payload.ttsPermissionsGranted;
    },
    setKPIs(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        const stats = action.payload.merchant?.merchantStats;
        state.merchant.merchantStats = stats;
        if (
          stats?.videoViews?.lastPeriodPercentageChange !== null ||
          stats?.videosPosted?.lastPeriodPercentageChange !== null ||
          stats?.productClicks?.lastPeriodPercentageChange !== null ||
          stats?.attributedGmv?.lastPeriodPercentageChange !== null
        ) {
          state.displayDashboard = true;
        } else {
          state.displayDashboard = false;
        }
      }
    },
    setTTSDashboardUrl(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        state.merchant.selfcareDashboardUrl = action.payload.merchant?.selfcareDashboardUrl;
      }
    },
    setCurrentTTSBillingPeriod(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        state.merchant.settingsObj = action.payload.merchant?.settingsObj;
        state.ttsTierRestrictions = handleMerchantTierRestrictions(action.payload.merchant?.metadata);
        if (state.merchant.onboarding) state.merchant.onboarding = { ...state.merchant.onboarding, ...action.payload.merchant?.onboarding };
        if (state.tiktokPlan) state.tiktokPlan = { ...state.tiktokPlan, ...action.payload.merchant?.plan };
      }
    },
    setGoogleAuthUrl(state, action: PayloadAction<MerchantState>) {
      if (state.merchant !== null) {
        state.merchant.googleAuthUrl = action.payload.merchant?.googleAuthUrl;
      }
    },
    setMerchantSettingsObj(state, action: PayloadAction<Merchant['settingsObj']>) {
      if (state.merchant !== null && action.payload != null) {
        state.merchant.settingsObj = action.payload;
        state.displayMessageCenter = new MerchantSettings(action.payload)
          .getSettingsBooleanAtPath(MerchantSettings.PATHS.dm_auto_reply);
      }
    }
  }
});

export const {
  grantTTSPermissions,
  setBasicInfo,
  setKPIs,
  setTTSDashboardUrl,
  setCurrentTTSBillingPeriod,
  setGoogleAuthUrl,
  setMerchantSettingsObj
} = merchantSlice.actions;

const handleMerchantTierRestrictions = (metadata?: string) => {
  if (metadata) {
    const followerRestrictions = extractFollowersTierFromMetadata(metadata);
    const unconnectedCreators = extractUnconnectedCreatorsTierRestriction(metadata);
    const isStarter = extractStarterTier(metadata);
    return { followers: followerRestrictions, unconnectedCreators, isStarter };
  }
};

// use cases
export async function queryMerchantInfo(): Promise<MerchantState> {
  const result = await authenticatedPost({ query: GET_MERCHANT_BASIC_INFO });
  if (result.data.merchant) {
    const state: MerchantState = {
      merchant: result.data.merchant,
      tiktokPlan: result.data.merchant.tiktokPlan,
      youtubePlan: result.data.merchant.youtubePlan
    };
    return state;
  }
  return { merchant: null };
}

export async function queryKPIs({ startDate, today }: { startDate: DateUtility; today: DateUtility }): Promise<MerchantState> {
  const result = await authenticatedPost({
    query: GET_KPIS,
    vars: {
      startDate: startDate.toISOFormattedString(),
      endDate: today.toISOFormattedString()
    }
  });

  if (result.data.merchant.merchantStats) {
    const state: MerchantState = { merchant: result.data.merchant };
    return state;
  }
  return { merchant: null };
}
